import { motion } from "framer-motion";
import { Calendar, Clock, User, Car, CheckCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { sanitizeUserInput, sanitizeEmail } from "@/utils/security";
import { Helmet } from "react-helmet";
import MainLayout from "@/components/layout/MainLayout";
import BackButton from "@/components/common/BackButton";

interface EmailJSResponse {
  status: number;
  text: string;
}

// Validation schema
const bookingSchema = z.object({
  services: z.array(z.string()).min(1, { message: "Wybierz co najmniej jedną usługę" }),
  date: z.string().nonempty({ message: "Wybierz datę" }),
  time: z.string().nonempty({ message: "Wybierz godzinę" }),
  name: z.string()
    .trim()
    .min(2, { message: "Imię musi mieć co najmniej 2 znaki" })
    .max(100, { message: "Imię nie może przekraczać 100 znaków" })
    .regex(/^[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ\s-]+$/, { message: "Imię zawiera niedozwolone znaki" }),
  phone: z.string()
    .trim()
    .min(9, { message: "Numer telefonu musi mieć co najmniej 9 cyfr" })
    .max(15, { message: "Numer telefonu nie może przekraczać 15 znaków" })
    .regex(/^[0-9\s\-+()]+$/, { message: "Nieprawidłowy format numeru telefonu" }),
  email: z.string()
    .trim()
    .email({ message: "Nieprawidłowy adres email" })
    .max(255, { message: "Email nie może przekraczać 255 znaków" }),
  carModel: z.string()
    .trim()
    .max(100, { message: "Model samochodu nie może przekraczać 100 znaków" })
    .optional(),
  notes: z.string()
    .trim()
    .max(1000, { message: "Uwagi nie mogą przekraczać 1000 znaków" })
    .optional()
});

// Email service hook
const useEmailService = () => {
  useEffect(() => {
    const loadEmailJS = async () => {
      const emailjs = await import('@emailjs/browser');
      emailjs.init(import.meta.env.VITE_EMAILJS_PUBLIC_KEY || "");
    };
    
    loadEmailJS();
  }, []);

  const sendBookingEmail = async (bookingData: any) => {
    const emailjs = await import('@emailjs/browser');
    
    const templateParams = {
      to_email: '061garageds@gmail.com',
      from_name: bookingData.name,
      from_email: bookingData.email,
      phone: bookingData.phone,
      car_model: bookingData.carModel,
      service: bookingData.service,
      date: bookingData.date,
      time: bookingData.time,
      notes: bookingData.notes,
      subject: `Nowa rezerwacja od ${bookingData.name}`,
      message: `
Nowa rezerwacja detailingu:

Klient: ${bookingData.name}
Email: ${bookingData.email}
Telefon: ${bookingData.phone}
Model samochodu: ${bookingData.carModel}

Wybrane usługi: ${bookingData.services.join(', ')}
Data: ${bookingData.date}
Godzina: ${bookingData.time}

Dodatkowe uwagi: ${bookingData.notes || 'Brak'}
      `
    };
    
    return emailjs.send(
      import.meta.env.VITE_EMAILJS_SERVICE_ID || "", 
      import.meta.env.VITE_EMAILJS_TEMPLATE_ID || "", 
      templateParams,
      import.meta.env.VITE_EMAILJS_PUBLIC_KEY || ""
    ) as Promise<EmailJSResponse>;
  };

  return { sendBookingEmail };
};

const Rezerwacja = () => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { sendBookingEmail } = useEmailService();
  const [formData, setFormData] = useState({
    services: [] as string[],
    date: "",
    time: "",
    name: "",
    phone: "",
    email: "",
    carModel: "",
    notes: ""
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Wszystkie usługi z cennika
  const services = [
    // Mycie zewnętrzne
    { id: "basic-wash", name: 'Mycie ,,Basic"', price: '79 zł', category: "exterior" },
    { id: "premium-wash", name: 'Mycie ,,Premium"', price: '119 zł', category: "exterior" },
    { id: "detailing-wash", name: 'Mycie detailingowe', price: '249 zł', category: "exterior" },
    
    // Detailing wnętrza
    { id: "basic-interior", name: 'Detailing wnętrza ,,Basic"', price: '99 zł', category: "interior" },
    { id: "plus-interior", name: 'Detailing wnętrza „Plus"', price: '249 zł', category: "interior" },
    { id: "ultra-interior", name: 'Detailing wnętrza „Ultra"', price: '499 zł', category: "interior" },
    
    // Pranie tapicerki
    { id: "seats-cleaning", name: 'Pranie foteli przednich + tylnych', price: '239 zł', category: "upholstery" },
    { id: "full-interior-cleaning", name: 'Pranie całego wnętrza', price: '399 zł', category: "upholstery" },
    { id: "leather-cleaning", name: 'Czyszczenie tapicerki skórzanej', price: '249 zł', category: "upholstery" },
    
    // Pakiety
    { id: "premium-package", name: 'Pakiet „Premium"', price: '499 zł', category: "packages" },
    { id: "complete-package", name: 'Pakiet „Detailing Kompletny"', price: '699 zł', category: "packages" },
    { id: "express-package", name: 'Pakiet „Express"', price: '159 zł', category: "packages" },
    
    // Usługi dodatkowe
    { id: "ozone", name: 'Ozonowanie wnętrza', price: '79 zł', category: "additional" },
    { id: "fabric-protection", name: 'Impregnacja tapicerki tekstylnej', price: '149 zł', category: "additional" },
    { id: "glass-coating", name: 'Powłoka hydrofobowa na szyby', price: '99 zł', category: "additional" },
    { id: "plastic-dressing", name: 'Dressingi plastików zewnętrznych', price: '79 zł', category: "additional" }
  ];

  const timeSlots = [
    "08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00"
  ];

  const handleInputChange = (field: string, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleServiceToggle = (serviceName: string) => {
    setFormData(prev => {
      const currentServices = prev.services;
      const isSelected = currentServices.includes(serviceName);
      
      return {
        ...prev,
        services: isSelected
          ? currentServices.filter(s => s !== serviceName)
          : [...currentServices, serviceName]
      };
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setValidationErrors({});
    
    try {
      const validatedData = bookingSchema.parse(formData);
      
      const sanitizedData = {
        ...validatedData,
        name: sanitizeUserInput(validatedData.name),
        email: sanitizeEmail(validatedData.email),
        phone: sanitizeUserInput(validatedData.phone),
        carModel: validatedData.carModel ? sanitizeUserInput(validatedData.carModel) : "",
        notes: validatedData.notes ? sanitizeUserInput(validatedData.notes) : ""
      };
      
      const response = await sendBookingEmail(sanitizedData);
      
      if (response.status === 200) {
        setStep(4);
        
        toast({
          title: "Rezerwacja wysłana!",
          description: "Skontaktujemy się z Tobą w ciągu 24h w celu potwierdzenia.",
          variant: "default"
        });
      } else {
        throw new Error("Wystąpił błąd podczas wysyłania rezerwacji.");
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        err.errors.forEach((error) => {
          if (error.path[0]) {
            errors[error.path[0] as string] = error.message;
          }
        });
        setValidationErrors(errors);
        toast({
          title: "Błąd walidacji",
          description: "Proszę sprawdzić poprawność wprowadzonych danych.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Błąd",
          description: "Wystąpił błąd podczas wysyłania rezerwacji. Spróbuj ponownie później.",
          variant: "destructive"
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = () => setStep(prev => Math.min(prev + 1, 4));
  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));

  // Grupowanie usług po kategorii
  const getServicesByCategory = (category: string) => {
    return services.filter(s => s.category === category);
  };

  const categoryNames: Record<string, string> = {
    exterior: "Mycie zewnętrzne",
    interior: "Detailing wnętrza",
    upholstery: "Pranie tapicerki",
    packages: "Pakiety usług",
    additional: "Usługi dodatkowe"
  };

  return (
    <>
      <Helmet>
        <title>Rezerwacja - 061 Garage Detailing Studio</title>
        <meta 
          name="description" 
          content="Zarezerwuj termin wizyty online w 061 Garage Detailing Studio. Wybierz usługę, datę i godzinę - proste i wygodne."
        />
        <meta name="keywords" content="rezerwacja detailingu, umów wizytę, booking detailing, rezerwacja online Poznań" />
      </Helmet>
      
      <MainLayout>
        <BackButton />
        <div className="min-h-screen bg-garage-dark pt-28 pb-12">
        <div className="container mx-auto px-6">
          <motion.h1
            className="text-3xl md:text-4xl font-bold text-center mb-4 text-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Rezerwacja online
          </motion.h1>
          
          <motion.p
            className="text-center text-gray-300 mb-12 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Umów wizytę w kilku prostych krokach. Wybierz usługę, datę i podaj swoje dane kontaktowe.
          </motion.p>

          <div className="max-w-4xl mx-auto">
            {/* Progress Bar */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold ${
                      step >= i ? "bg-garage-blue text-white" : "bg-garage-gray/30 text-gray-400"
                    }`}
                  >
                    {i}
                  </div>
                ))}
              </div>
              <div className="w-full bg-garage-gray/30 rounded-full h-2">
                <div
                  className="bg-garage-blue h-2 rounded-full transition-all duration-300"
                  style={{ width: `${((step - 1) / 2) * 100}%` }}
                />
              </div>
            </div>

            <div className="bg-garage-gray/20 p-8 rounded-lg border border-garage-blue/20">
              {/* Step 1: Service Selection */}
              {step === 1 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <h3 className="text-xl font-semibold mb-6 flex items-center">
                    <Car className="h-5 w-5 text-garage-blue mr-2" />
                    Wybierz usługi (możesz wybrać kilka)
                  </h3>
                  
                  {Object.keys(categoryNames).map(category => {
                    const categoryServices = getServicesByCategory(category);
                    if (categoryServices.length === 0) return null;
                    
                    return (
                      <div key={category} className="mb-6">
                        <h4 className="text-lg font-semibold text-garage-blue mb-3">
                          {categoryNames[category]}
                        </h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          {categoryServices.map((service) => {
                            const isSelected = formData.services.includes(service.name);
                            return (
                              <div
                                key={service.id}
                                onClick={() => handleServiceToggle(service.name)}
                                className={`p-4 border rounded-lg cursor-pointer transition-all ${
                                  isSelected
                                    ? "border-garage-blue bg-garage-blue/10"
                                    : "border-garage-blue/30 hover:border-garage-blue/50"
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className={`mt-1 w-5 h-5 border-2 rounded flex items-center justify-center ${
                                    isSelected ? "bg-garage-blue border-garage-blue" : "border-garage-blue/50"
                                  }`}>
                                    {isSelected && (
                                      <svg className="w-3 h-3 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                        <path d="M5 13l4 4L19 7"></path>
                                      </svg>
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <h5 className="font-semibold text-white mb-1">{service.name}</h5>
                                    <p className="text-garage-blue text-sm">{service.price}</p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  
                  <div className="flex justify-end mt-6">
                    <button
                      onClick={nextStep}
                      disabled={formData.services.length === 0}
                      className="bg-garage-blue hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg transition-colors"
                    >
                      Dalej
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Date & Time */}
              {step === 2 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <h3 className="text-xl font-semibold mb-6 flex items-center">
                    <Calendar className="h-5 w-5 text-garage-blue mr-2" />
                    Wybierz datę i godzinę
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium mb-2">Data</label>
                      <input
                        type="date"
                        value={formData.date}
                        onChange={(e) => handleInputChange("date", e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full bg-garage-gray/30 border border-garage-blue/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-garage-blue"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Godzina</label>
                      <div className="grid grid-cols-3 gap-2">
                        {timeSlots.map((time) => (
                          <button
                            key={time}
                            onClick={() => handleInputChange("time", time)}
                            className={`p-2 text-sm rounded border transition-all ${
                              formData.time === time
                                ? "border-garage-blue bg-garage-blue/10 text-garage-blue"
                                : "border-garage-blue/30 text-gray-300 hover:border-garage-blue/50"
                            }`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <button
                      onClick={prevStep}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg transition-colors"
                    >
                      Wstecz
                    </button>
                    <button
                      onClick={nextStep}
                      disabled={!formData.date || !formData.time}
                      className="bg-garage-blue hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg transition-colors"
                    >
                      Dalej
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Contact Details */}
              {step === 3 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <h3 className="text-xl font-semibold mb-6 flex items-center">
                    <User className="h-5 w-5 text-garage-blue mr-2" />
                    Dane kontaktowe
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium mb-2">Imię i nazwisko *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        className={`w-full bg-garage-gray/30 border rounded-lg px-4 py-3 text-white focus:outline-none focus:border-garage-blue ${
                          validationErrors.name ? "border-red-500" : "border-garage-blue/30"
                        }`}
                        placeholder="Jan Kowalski"
                        maxLength={100}
                      />
                      {validationErrors.name && (
                        <p className="text-red-500 text-sm mt-1">{validationErrors.name}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Telefon *</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange("phone", e.target.value)}
                        className={`w-full bg-garage-gray/30 border rounded-lg px-4 py-3 text-white focus:outline-none focus:border-garage-blue ${
                          validationErrors.phone ? "border-red-500" : "border-garage-blue/30"
                        }`}
                        placeholder="123 456 789"
                        maxLength={15}
                      />
                      {validationErrors.phone && (
                        <p className="text-red-500 text-sm mt-1">{validationErrors.phone}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Email *</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        className={`w-full bg-garage-gray/30 border rounded-lg px-4 py-3 text-white focus:outline-none focus:border-garage-blue ${
                          validationErrors.email ? "border-red-500" : "border-garage-blue/30"
                        }`}
                        placeholder="jan@example.com"
                        maxLength={255}
                      />
                      {validationErrors.email && (
                        <p className="text-red-500 text-sm mt-1">{validationErrors.email}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Model samochodu</label>
                      <input
                        type="text"
                        value={formData.carModel}
                        onChange={(e) => handleInputChange("carModel", e.target.value)}
                        className={`w-full bg-garage-gray/30 border rounded-lg px-4 py-3 text-white focus:outline-none focus:border-garage-blue ${
                          validationErrors.carModel ? "border-red-500" : "border-garage-blue/30"
                        }`}
                        placeholder="BMW X5 2020"
                        maxLength={100}
                      />
                      {validationErrors.carModel && (
                        <p className="text-red-500 text-sm mt-1">{validationErrors.carModel}</p>
                      )}
                    </div>
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-2">Dodatkowe uwagi</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => handleInputChange("notes", e.target.value)}
                      rows={3}
                      className={`w-full bg-garage-gray/30 border rounded-lg px-4 py-3 text-white focus:outline-none focus:border-garage-blue ${
                        validationErrors.notes ? "border-red-500" : "border-garage-blue/30"
                      }`}
                      placeholder="Opowiedz nam o stanie Twojego samochodu..."
                      maxLength={1000}
                    />
                    {validationErrors.notes && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.notes}</p>
                    )}
                  </div>
                  <div className="flex justify-between">
                    <button
                      onClick={prevStep}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg transition-colors"
                    >
                      Wstecz
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={!formData.name || !formData.phone || !formData.email || isSubmitting}
                      className="bg-garage-blue hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg transition-colors flex items-center"
                    >
                      {isSubmitting ? "Wysyłanie..." : "Wyślij rezerwację"}
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Step 4: Confirmation */}
              {step === 4 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="text-center"
                >
                  <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-semibold mb-4">Dziękujemy za rezerwację!</h3>
                  <p className="text-gray-300 mb-6">
                    Twoja rezerwacja została wysłana. Skontaktujemy się z Tobą w ciągu 24h w celu potwierdzenia.
                  </p>
                  <button
                    onClick={() => {
                      setStep(1);
                      setFormData({
                        services: [],
                        date: "",
                        time: "",
                        name: "",
                        phone: "",
                        email: "",
                        carModel: "",
                        notes: ""
                      });
                    }}
                    className="bg-garage-blue hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Nowa rezerwacja
                  </button>
                </motion.div>
              )}
            </div>
          </div>
        </div>
        </div>
      </MainLayout>
    </>
  );
};

export default Rezerwacja;
